import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';

interface HourSelectProps {
  value: number;
  onChange: (hour: number) => void;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
}

interface PanelPosition {
  top: number;
  left: number;
  width: number;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function formatHour(hour: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${display}:00 ${period}`;
}

export default function HourSelect({
  value,
  onChange,
  disabled,
  id,
  ariaLabel,
}: HourSelectProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      panelRef.current
        ?.querySelector<HTMLElement>(`[data-value="${value}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    });
  }, [open, value]);

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className="w-full inline-flex items-center justify-between rounded-xl bg-input-bg border-2 border-border px-3 py-2 font-sans text-sm font-medium text-text-primary outline-none focus:border-primary transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span>{formatHour(value)}</span>
        <ChevronDown
          size={14}
          className={`text-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {open && position && (
              <motion.div
                ref={panelRef}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.12 }}
                style={{
                  position: 'absolute',
                  top: position.top,
                  left: position.left,
                  width: position.width,
                }}
                className="z-50 max-h-64 overflow-y-auto rounded-xl bg-card border-2 border-border shadow-lg"
                role="listbox"
              >
                <ul className="py-1">
                  {HOURS.map((h) => {
                    const isSelected = h === value;
                    return (
                      <li
                        key={h}
                        data-value={h}
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => {
                          onChange(h);
                          setOpen(false);
                        }}
                        className={`flex items-center justify-between px-3 py-2 font-sans text-sm cursor-pointer hover:bg-cream-warm transition-colors ${
                          isSelected ? 'text-primary font-semibold' : 'text-text-primary'
                        }`}
                      >
                        <span>{formatHour(h)}</span>
                        {isSelected && (
                          <Check size={14} className="text-primary" aria-hidden />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}
